export function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return 'id-' + Math.random().toString(36).slice(2, 11);
}

export function chunkArray<T>(items: T[], chunkSize: number): T[][] {
  if (chunkSize <= 0) {
    return [items];
  }

  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  return chunks;
}

export function clamp(value: number, min = 0, max = 1) {
  return Math.min(Math.max(value, min), max);
}

export function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const num = bytes / Math.pow(k, i);
  return `${num.toFixed(num > 10 ? 0 : 1)} ${sizes[i]}`;
}

export function createElement<T extends keyof HTMLElementTagNameMap>(
  tag: T,
  className?: string,
  textContent?: string
): HTMLElementTagNameMap[T] {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (textContent) element.textContent = textContent;
  return element;
}

export function resolveIcon(renderer: string | HTMLElement | (() => string | HTMLElement)) {
  if (typeof renderer === 'function') {
    return resolveIcon(renderer());
  }
  if (typeof renderer === 'string') {
    const template = document.createElement('template');
    template.innerHTML = renderer.trim();
    return template.content.firstElementChild as HTMLElement | null;
  }
  return renderer ? (renderer.cloneNode(true) as HTMLElement) : null;
}

export function toCssVars(theme: {
  appearance?: 'light' | 'dark' | 'system';
  accentColor?: string;
  borderRadius?: number;
  fontFamily?: string;
  dropzoneBackground?: string;
  dropzoneBorder?: string;
}) {
  const variables: Record<string, string> = {};
  if (theme.accentColor) {
    variables['--ar-accent'] = theme.accentColor;
  }
  if (theme.borderRadius !== undefined) {
    variables['--ar-radius'] = `${theme.borderRadius}px`;
  }
  if (theme.fontFamily) {
    variables['--ar-font-family'] = theme.fontFamily;
  }
  if (theme.dropzoneBackground) {
    variables['--ar-dropzone-bg'] = theme.dropzoneBackground;
  }
  if (theme.dropzoneBorder) {
    variables['--ar-dropzone-border'] = theme.dropzoneBorder;
  }
  return variables;
}

export function applyCssVariables(
  element: HTMLElement,
  variables: Record<string, string>
) {
  Object.entries(variables).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });
}

export function getRelativeFolderPath(
  basePath: string | undefined,
  relativePath: string | undefined
) {
  if (!relativePath) return undefined;

  const parts = relativePath
    .split('/')
    .map(segment => segment.trim())
    .filter(segment => segment && segment !== '.');

  if (parts.length <= 1) {
    return undefined;
  }

  const folderSegments = parts.slice(0, -1);

  if (basePath) {
    const baseSegments = basePath
      .split('/')
      .map(segment => segment.trim())
      .filter(segment => segment && segment !== '.');

    let offset = 0;
    while (
      offset < baseSegments.length &&
      offset < folderSegments.length &&
      folderSegments[offset] === baseSegments[offset]
    ) {
      offset += 1;
    }

    return offset < folderSegments.length
      ? folderSegments.slice(offset).join('/')
      : undefined;
  }

  return folderSegments.length ? folderSegments.join('/') : undefined;
}

export function calculateBatchProgress(items: { progress: number }[]) {
  if (items.length === 0) return 0;
  const total = items.reduce((acc, item) => acc + item.progress, 0);
  return Math.round(total / items.length);
}

const S3_URL_PATTERN = /^s3:\/\/([^/]+)\/(.+)$/i;

export function sanitizeBaseUrl(baseUrl?: string): string | undefined {
  if (!baseUrl) return undefined;
  const trimmed = baseUrl.trim();
  if (!trimmed) return undefined;
  return trimmed.replace(/\/+$/, '');
}

export function extractWorkspaceFromUrl(url?: string): string | undefined {
  if (!url) return undefined;
  const s3Match = S3_URL_PATTERN.exec(url);
  if (s3Match) {
    return s3Match[1];
  }
  try {
    const parsed = new URL(url);
    const firstSegment = parsed.pathname.replace(/^\/+/, '').split('/')[0];
    return firstSegment || undefined;
  } catch {
    return undefined;
  }
}

export function extractPathFromUrl(url?: string): string | undefined {
  if (!url) return undefined;
  const s3Match = S3_URL_PATTERN.exec(url);
  if (s3Match) {
    return s3Match[2];
  }
  try {
    const parsed = new URL(url);
    const [, ...rest] = parsed.pathname.replace(/^\/+/, '').split('/');
    return rest.length ? rest.join('/') : undefined;
  } catch {
    return undefined;
  }
}

export function buildAssetDeliveryUrl(
  baseUrl: string | undefined,
  workspace?: string,
  path?: string,
  fallback?: string
): string | undefined {
  const sanitizedBase = sanitizeBaseUrl(baseUrl);
  if (!sanitizedBase || !workspace || !path) {
    return fallback;
  }

  const normalizedPath = path
    .split('/')
    .filter(Boolean)
    .map(segment => encodeURIComponent(segment))
    .join('/');

  return `${sanitizedBase}/${workspace}/${normalizedPath}`.replace(/([^:]\/)\/+/g, '$1');
}

